<template>
  <div>
    <div class="flex justify-between title-y-axis">
      <p class="left">{{ titleY }}</p>
      <p class="right">{{ titleYOpposite }}</p>
    </div>
    <div class="chart-wrapper">
      <apexchart type="line" :options="chartOptions" :series="updatedSeries" height="337" class="mixed-chart-mobile elsa-chart" />
    </div>
  </div>
</template>

<script>
import { toFixed, lensMatching, round } from '../../../utils/ramda-utils'
import { propEq, propOr, compose, converge, reduce, head, identity, view, max, min, curry, map, lensProp, over, sortBy, prop } from 'ramda'
import { ORANGE_GAME, DEFAULT_FONT, TRANSPARENT, DASHLINE } from '@/constants/charts'

const lensByName = compose(lensMatching, propEq('name'))
const maxValue = curry((propName, context) => {
  const maxValue = compose(converge(reduce(max), [head, identity]), propOr([], 'data'), view(lensByName(propName)), propOr([], 'series'))(context)

  return maxValue
})

const minValue = curry((propName, context) => {
  const minValue = compose(converge(reduce(min), [head, identity]), propOr([], 'data'), view(lensByName(propName)), propOr([], 'series'))(context)

  return minValue
})

const xAxisColorOrange = '#EF5F00'
const xAxisColorBlue = '#478DFF'

export default {
  props: {
    series: {
      type: Array,
      default: () => [
        {
          name: 'minutes_practiced',
          type: 'line',
          data: []
        },
        {
          name: 'lessones_practices',
          type: 'column',
          data: []
        }
      ]
    },
    labels: {
      type: Array,
      default: () => []
    },
    titleY: {
      type: String,
      default: ''
    },
    titleYOpposite: {
      type: String,
      default: ''
    },
    goal: {
      type: Number,
      default: 0
    },
    showGoal: {
      type: Boolean,
      default: false
    },
    organizationType: {
      type: String,
      default: 'school'
    }
  },
  computed: {
    discrete() {
      const ret = []
      if (this.series && this.series.length > 0 && this.series[0].data && this.series[0].data.length > 0) {
        this.series[0].data.forEach((v, k) => {
          if (v === 0) {
            ret.push({
              seriesIndex: 0,
              dataPointIndex: k,
              size: 0
            })
          }
        })
      }
      return ret
    },
    updatedSeries() {
      let series = compose(map(over(lensProp('name'), (name) => this.$t(`charts.${name}`))), propOr([], 'series'))(this)
      return series
    },
    maxNumberOfLessons: maxValue('lessones_practices'),
    minNumberOfLessons: minValue('lessones_practices'),
    maxNumberOfMinutes: maxValue('minutes_practiced'),
    minNumberOfMinutes: minValue('minutes_practiced'),
    numberOfLessonsTickAmount() {
      let ticks = this.maxNumberOfLessons < 6 ? round(this.maxNumberOfLessons) : 6
      if (ticks <= 6) {
        ticks = 6
      }
      return parseInt(ticks)
    },
    numberOfMinutesTickAmount() {
      let ticks = this.maxNumberOfMinutes < 6 ? round(this.maxNumberOfMinutes) : 6
      if (ticks <= 6) {
        ticks = 6
      }
      return parseInt(ticks)
    },
    maxNumberOfMinutesAndGoal() {
      let ret = this.maxNumberOfMinutes
      if (this.showGoal) {
        ret = this.goal > this.maxNumberOfMinutes ? this.goal : this.maxNumberOfMinutes
      }
      return ret
    },
    minNumberOfMinutesAndGoal() {
      let ret = this.minNumberOfMinutes
      if (this.showGoal) {
        ret = this.goal < this.minNumberOfMinutes ? this.goal : this.minNumberOfMinutes
      }
      return ret
    },
    annotations() {
      if (!this.showGoal) {
        return {}
      }
      return {
        yaxis: [
          {
            y: this.goal,
            strokeDashArray: 10,
            borderColor: DASHLINE,
            label: {
              textAnchor: 'start',
              position: 'left',
              borderColor: '#6D7A8C',
              style: {
                background: '#6D7A8C',
                fontWeight: 600,
                color: '#fff',
                fontSize: '14px',
                fontFamily: DEFAULT_FONT
              },
              text: this.$t(this.$i18nLabelsHelper(this.organizationType, 'charts.team_study_goal'))
            }
          }
        ]
      }
    },
    chartOptions() {
      return {
        chart: {
          animations: {
            enabled: true
          },
          type: 'line',
          toolbar: {
            show: false
          },
          width: '100%'
        },
        stroke: {
          width: [4, 0]
        },
        // tooltip:{
        //   shared: false,
        // },
        dataLabels: {
          enabled: false,
          enabledOnSeries: [0],
          formatter: function (value) {
            if (value === 0) {
              return ''
            } else {
              return value.toFixed(0)
            }
          },
          offsetY: 0.5,
          background: {
            borderWidth: 0
          },
          dropShadow: {
            enabled: false
          },
          style: {
            fontSize: '16px',
            fontFamily: DEFAULT_FONT,
            fontWeight: 600
          }
        },
        xaxis: {
          type: 'category',
          categories: this.labels,
          labels: {
            show: true,
            offsetY: 5
          }
        },
        colors: [ORANGE_GAME, xAxisColorBlue],
        fill: {
          type: ['solid', 'gradient'],
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 1,
            colorStops: [
              {
                offset: 0,
                color: '#29F4FF',
                opacity: 1
              },
              {
                offset: 100,
                color: '#5257FF',
                opacity: 1
              }
            ]
          }
        },
        annotations: this.annotations,
        yaxis: [
          {
            tickAmount: this.numberOfMinutesTickAmount,
            max: (maxV) => {
              let ret = maxV
              if (this.showGoal && maxV < this.goal) {
                ret = this.goal
              }
              return ret
            },
            min: (min) => {
              let ret = min
              if (this.showGoal && min > this.goal) {
                ret = this.goal
              }
              if (this.maxNumberOfMinutes == 0) {
                ret = 0
              }
              return ret
            },
            labels: {
              formatter: toFixed(0),
              //align: 'right',
              style: {
                colors: xAxisColorOrange,
                fontSize: '16px',
                fontFamily: DEFAULT_FONT,
                fontWeight: 600,
                cssClass: 'apexcharts-yaxis-label'
              }
            },
            title: {
              text: '',
              // align: 'left',
              style: {
                //   color: xAxisColorOrange,
                fontSize: '0px'
                //   fontFamily: DEFAULT_FONT,
                //   fontWeight: 600
              }
            },
            axisBorder: {
              show: false
              // color: xAxisColorOrange
            }
          },
          {
            opposite: true,
            tickAmount: this.numberOfLessonsTickAmount,
            labels: {
              formatter: toFixed(0),
              align: 'right',
              style: {
                colors: xAxisColorBlue,
                fontSize: '16px',
                fontFamily: DEFAULT_FONT,
                fontWeight: 600
              }
            },
            title: {
              text: '',
              // align: 'left',
              style: {
                //   color: xAxisColorBlue,
                fontSize: '0px'
                //   fontFamily: DEFAULT_FONT,
                //   fontWeight: 600,
                //   cssClass: 'apexcharts-yaxis-label'
              }
            },
            axisBorder: {
              show: false
              // color: xAxisColorBlue
            }
          }
        ],
        markers: {
          size: 8,
          strokeWidth: 0,
          fillOpacity: 0,
          strokeOpacity: 0,
          colors: [ORANGE_GAME],
          discrete: this.discrete,
          hover: {
            sizeOffset: 0
          }
        },
        states: {
          hover: {
            filter: {
              type: 'none'
            }
          }
        },
        legend: {
          fontSize: '16px',
          fontFamily: DEFAULT_FONT,
          fontWeight: 400,
          itemMargin: {
            vertical: 15,
            horizontal: 45
          },
          labels: {
            colors: ['#121131']
          },
          markers: {
            fillColors: [[ORANGE_GAME], ['#29F4FF', '#5257FF']]
          }
        },
        grid: {
          show: false
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mixed-chart-mobile {
  min-width: 600px;
}
.elsa-chart {
  margin-top: auto;
  margin-bottom: auto;
  display: flex;
  align-items: center;
  align-content: center;
  flex-direction: row;
  min-width: 100%;
}

.chart-wrapper {
  height: 350px;
  display: flex;
  flex-direction: column;
}

::v-deep .apexcharts-svg {
  // overflow: visible;
}

.title-y-axis {
  margin-bottom: -1.5em;
  font-size: 18px;
  font-weight: 600;
  padding-left: 16px;
  padding-right: 32px;
  .left {
    color: #ef5f00;
  }

  .right {
    color: #478dff;
  }
}
</style>
